// Roles (FD §10): the list, and a drawer to create, edit, retire and look
// back through a role's versions. Every save creates a new, immutable
// version (FD §10.2): agents stay on the version they were set up with until
// they're moved on, which the drawer offers right after saving.

import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { DEFAULT_ROLE_RUN_SETTINGS, type RoleRunSettings } from '@goblic/shared'
import {
  ApiError,
  agentsApi,
  mcpServicesApi,
  rolesApi,
  searchConnectionsApi,
  skillsApi,
  SEARCH_PROVIDER_LABEL,
  type RoleDetail,
  type RoleSettings,
  type RoleVersionContent,
  type RoleVersionRecord,
} from '../lib/api-client'
import { StatusBadge } from '../components/status-badge'
import { RoleQuotaSettings } from '../components/role-quota-settings'
import { ModelPicker } from '../components/model-picker'
import { useDialogs } from '../components/dialogs'
import { kbSlug } from '../components/knowledge/kb-types'
import {
  Drawer,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  Notice,
  PageHeader,
} from '../components/ui'
import { useWorkspaceId } from '../lib/auth'
import { useIllustrations } from '../lib/illustrations'
import { formatDateTime, formatRelative } from '../lib/format'
import {
  ClayIcon,
  RoleAvatar,
  type ClayIconName,
} from '../components/goblin-art'

type RiskLevel = RoleSettings['allowedRiskLevel']

const RISK_LEVELS: { value: RiskLevel; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

/** Actions a role can always put behind an approval (the workspace rules in Settings apply on top). */
const APPROVAL_ACTIONS: {
  value: string
  icon: ClayIconName
  label: string
}[] = [
  { value: 'branch_push', icon: 'push', label: 'Pushing a branch' },
  {
    value: 'pull_request_create_or_ready',
    icon: 'pull-request',
    label: 'Opening a pull request',
  },
  {
    value: 'new_network_domain',
    icon: 'signpost',
    label: 'Reaching a website not on the list',
  },
  {
    value: 'follow_up_agent',
    icon: 'gears',
    label: 'Handing work to another agent',
  },
  {
    value: 'deployment_merge_or_release',
    icon: 'rocket',
    label: 'Merging, deploying or releasing',
  },
]

const EXECUTORS = [
  { value: '', label: 'No preference' },
  { value: 'claude_cli', label: 'Claude Code' },
  { value: 'codex_cli', label: 'Codex' },
  { value: 'openai_compatible', label: 'OpenAI-compatible API' },
]

interface RoleFormValues extends RoleVersionContent {
  name: string
  description: string
  allowedRiskLevel: RiskLevel
  defaultExecutorType: string
  defaultModel: string
}

const BLANK: RoleFormValues = {
  name: '',
  description: '',
  baseInstructions: '',
  workload: 'heavy',
  allowedRiskLevel: 'low',
  approvalRequiredActions: [],
  skillIds: [],
  allowedMcpServiceIds: [],
  runSettings: DEFAULT_ROLE_RUN_SETTINGS,
  defaultExecutorType: '',
  defaultModel: '',
}

/**
 * Versions saved before a settings block existed (kb, reports, tools,
 * checkpoints …) lack it; fill each block from the defaults so an old,
 * retired version still opens.
 */
function withRunSettingDefaults(saved: RoleFormValues['runSettings'] | null | undefined): RoleFormValues['runSettings'] {
  if (!saved) return DEFAULT_ROLE_RUN_SETTINGS
  const d = DEFAULT_ROLE_RUN_SETTINGS
  return {
    ...d,
    ...saved,
    web: { ...d.web, ...saved.web },
    delegate: { ...d.delegate, ...saved.delegate },
    tools: { ...d.tools, ...saved.tools },
    kb: { ...d.kb, ...saved.kb },
    reports: { ...d.reports, ...saved.reports },
    limits: { ...d.limits, ...saved.limits },
    onQuota: { ...d.onQuota, ...saved.onQuota },
  }
}

function fromDetail(detail: RoleDetail): RoleFormValues {
  return {
    name: detail.name,
    description: detail.description ?? '',
    baseInstructions: detail.current.baseInstructions,
    workload: detail.current.workload,
    allowedRiskLevel: detail.allowedRiskLevel,
    approvalRequiredActions: detail.current.approvalRequiredActions ?? [],
    skillIds: detail.current.skillIds ?? [],
    allowedMcpServiceIds: detail.current.allowedMcpServiceIds ?? [],
    // Versions saved before the knowledge base existed carry no `kb` block.
    // Versions saved before checkpoints carry no `onQuota`/`maxTransientRetries`.
    runSettings: withRunSettingDefaults(detail.current.runSettings),
    defaultExecutorType: detail.defaultExecutorType ?? '',
    defaultModel: detail.defaultModel ?? '',
  }
}

/** The request body: optional defaults left out rather than sent empty. */
function toBody(values: RoleFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim() || undefined,
    baseInstructions: values.baseInstructions.trim(),
    workload: values.workload,
    allowedRiskLevel: values.allowedRiskLevel,
    // Long-term memory moved to the knowledge base; the option is gone, so
    // drop it from roles that still carry it (the API filters it as well).
    approvalRequiredActions: values.approvalRequiredActions.filter(
      (action) => action !== 'persistent_memory_save'
    ),
    skillIds: values.skillIds,
    allowedMcpServiceIds: values.allowedMcpServiceIds,
    runSettings: values.runSettings,
    defaultExecutorType: values.defaultExecutorType || undefined,
    defaultModel: values.defaultModel.trim() || undefined,
  }
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

export function RolesPage() {
  const workspaceId = useWorkspaceId()
  const [open, setOpen] = useState<string | 'new' | null>(null)
  const rolesQuery = useQuery({
    queryKey: ['roles', workspaceId],
    queryFn: () => rolesApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const allRoles = rolesQuery.data?.items ?? []
  const [showRetired, setShowRetired] = useState(false)
  const retiredCount = allRoles.filter((r) => r.isDeactivated).length
  const roles = showRetired ? allRoles : allRoles.filter((r) => !r.isDeactivated)

  return (
    <div>
      <PageHeader
        title="Roles"
        description="A role is a job description for an agent: its instructions, skills, tools and limits. Every edit is saved as a new version."
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setOpen('new')}
          >
            New role
          </button>
        }
      />
      {retiredCount > 0 ? (
        <label className="mb-3 flex items-center gap-2 text-sm text-ink-2">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          Show retired roles ({retiredCount})
        </label>
      ) : null}
      <div className="panel overflow-x-auto">
        {rolesQuery.isLoading ? (
          <LoadingRows />
        ) : rolesQuery.isError ? (
          <ErrorNote error={rolesQuery.error} what="roles" />
        ) : allRoles.length === 0 ? (
          <EmptyState
            title="No roles yet"
            action={
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setOpen('new')}
              >
                Create a role
              </button>
            }
          >
            A role tells an agent what its job is. Start with a developer.
          </EmptyState>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Runs</th>
                <th>Version</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() => setOpen(role.id)}
                      className="group flex items-center gap-3 text-left"
                    >
                      <RoleAvatar roleName={role.name} size={40} />
                      <span className="min-w-0">
                        <span className="block font-medium text-ink group-hover:underline">
                          {role.name}
                        </span>
                        {role.description ? (
                          <span className="block max-w-lg truncate text-sm text-ink-3">
                            {role.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </td>
                  <td className="whitespace-nowrap">
                    {role.workload === 'light' ? (
                      <span title="Mostly waits on the model. Many can run at once in a small sandbox.">
                        Light
                      </span>
                    ) : (
                      <span title="Builds and tests code. Gets a full sandbox and one of the few heavy slots.">
                        Builds &amp; tests
                      </span>
                    )}
                  </td>
                  <td className="tabular-nums">v{role.currentVersion}</td>
                  <td>
                    <StatusBadge
                      status={role.isDeactivated ? 'inactive' : 'active'}
                      label={role.isDeactivated ? 'Retired' : 'In use'}
                    />
                  </td>
                  <td className="whitespace-nowrap">
                    {formatRelative(role.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RoleDrawer
        key={open ?? 'closed'}
        workspaceId={workspaceId}
        roleId={open}
        otherRoles={roles.map((r) => ({ id: r.id, name: r.name }))}
        onClose={() => setOpen(null)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

function RoleDrawer({
  workspaceId,
  roleId,
  otherRoles,
  onClose,
}: {
  workspaceId: string
  roleId: string | 'new' | null
  otherRoles: { id: string; name: string }[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const isNew = roleId === 'new'
  const [tab, setTab] = useState<'settings' | 'history'>('settings')
  const [values, setValues] = useState<RoleFormValues>(BLANK)
  const [startFrom, setStartFrom] = useState('')
  /** After saving: agents still on an older version of this role. */
  const [savedVersion, setSavedVersion] = useState<number | null>(null)

  const detailQuery = useQuery({
    queryKey: ['role', roleId, workspaceId],
    queryFn: () => rolesApi.get(roleId as string, workspaceId),
    enabled: roleId != null && !isNew,
  })
  const detail = detailQuery.data

  // Fill the form once the role is loaded.
  useEffect(() => {
    if (detail && !isNew) setValues(fromDetail(detail))
  }, [detail, isNew])

  // "Start from" copies another role's current content into a new one.
  const templateQuery = useQuery({
    queryKey: ['role', startFrom, workspaceId],
    queryFn: () => rolesApi.get(startFrom, workspaceId),
    enabled: isNew && startFrom !== '',
  })
  useEffect(() => {
    if (isNew && templateQuery.data) {
      const copy = fromDetail(templateQuery.data)
      // Fallback agents belong to the other role; a new role has none yet.
      setValues({
        ...copy,
        name: `${copy.name} (copy)`,
        runSettings: {
          ...copy.runSettings,
          onQuota: { ...copy.runSettings.onQuota, fallbackAgentIds: [] },
        },
      })
    }
  }, [isNew, templateQuery.data])

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['roles', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['role', roleId] }),
      queryClient.invalidateQueries({ queryKey: ['role-versions', roleId] }),
    ])

  const save = useMutation({
    mutationFn: async () => {
      const body = { workspaceId, ...toBody(values) }
      if (isNew) await rolesApi.create(body)
      else await rolesApi.update(roleId as string, body)
    },
    onSuccess: async () => {
      await refresh()
      if (isNew) {
        onClose()
        return
      }
      const fresh = await rolesApi.get(roleId as string, workspaceId)
      setSavedVersion(fresh.current.version)
    },
  })

  const retire = useMutation({
    mutationFn: () => rolesApi.retire(roleId as string, workspaceId),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t retire the role',
        message: errorText(error, ''),
      }),
  })

  const remove = useMutation({
    mutationFn: () => rolesApi.remove(roleId as string, workspaceId),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t delete the role',
        message: errorText(error, ''),
      }),
  })

  async function handleDelete(): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Delete ${values.name || 'this role'} permanently?`,
      message: 'The role and all its versions are removed. This can’t be undone.',
      confirmLabel: 'Delete role',
    })
    if (ok) remove.mutate()
  }

  async function handleRetire(): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Retire ${values.name || 'this role'}?`,
      message:
        'No new agents can be set up with it. Agents that already use it keep working, and its history stays.',
      confirmLabel: 'Retire role',
    })
    if (ok) retire.mutate()
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    setSavedVersion(null)
    save.mutate()
  }

  const canSave =
    values.name.trim() !== '' && values.baseInstructions.trim() !== ''
  const nextVersion = (detail?.current.version ?? 0) + 1

  return (
    <Drawer
      open={roleId != null}
      onClose={onClose}
      width="max-w-2xl"
      title={isNew ? 'New role' : (detail?.name ?? 'Role')}
      description={
        isNew
          ? 'Describe the job. Agents are set up with a role; you can change it later.'
          : detail
            ? `Version ${detail.current.version}, saved ${formatRelative(detail.current.createdAt)}.`
            : undefined
      }
      footer={
        tab === 'settings' && (isNew || detail) ? (
          <div className="flex items-center justify-between gap-3">
            {!isNew && !detail?.isDeactivated ? (
              <button
                type="button"
                className="btn-danger-quiet btn-sm"
                onClick={() => void handleRetire()}
                disabled={retire.isPending}
              >
                Retire role
              </button>
            ) : !isNew ? (
              <button
                type="button"
                className="btn-danger-quiet btn-sm"
                onClick={() => void handleDelete()}
                disabled={remove.isPending}
              >
                Delete role
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button type="button" className="btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                form="role-form"
                className="btn-primary"
                disabled={!canSave || save.isPending}
              >
                {save.isPending
                  ? 'Saving…'
                  : isNew
                    ? 'Create role'
                    : `Save as version ${nextVersion}`}
              </button>
            </div>
          </div>
        ) : null
      }
    >
      {!isNew ? (
        <div
          role="tablist"
          aria-label="Role"
          className="mb-5 flex gap-1 border-b border-line"
        >
          {(
            [
              ['settings', 'Settings'],
              ['history', 'History'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={`-mb-px border-b-2 px-3 py-2 text-base font-medium ${
                tab === value
                  ? 'border-action text-ink'
                  : 'border-transparent text-ink-3 hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {!isNew && detailQuery.isLoading ? (
        <LoadingRows />
      ) : !isNew && detailQuery.isError ? (
        <ErrorNote error={detailQuery.error} what="this role" />
      ) : tab === 'history' && !isNew && detail ? (
        <RoleHistory
          workspaceId={workspaceId}
          roleId={detail.id}
          currentVersion={detail.current.version}
          onRestored={async () => {
            await refresh()
            setTab('settings')
          }}
        />
      ) : (
        <div className="space-y-5">
          {savedVersion != null && !isNew ? (
            <MoveAgentsNotice
              workspaceId={workspaceId}
              roleId={roleId as string}
              version={savedVersion}
              currentVersionId={detail?.current.id ?? ''}
            />
          ) : null}
          {isNew && otherRoles.length ? (
            <Field
              label="Start from"
              htmlFor="role-start-from"
              hint="Copies another role’s instructions and settings, so you only change what’s different."
            >
              <select
                id="role-start-from"
                className="field-input"
                value={startFrom}
                onChange={(event) => setStartFrom(event.target.value)}
              >
                <option value="">A blank role</option>
                {otherRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <RoleForm
            workspaceId={workspaceId}
            roleId={isNew ? null : roleId}
            values={values}
            onChange={setValues}
            onSubmit={handleSubmit}
          />
          {save.isError ? (
            <Notice tone="error">
              {errorText(save.error, 'Couldn’t save the role.')}
            </Notice>
          ) : null}
        </div>
      )}
    </Drawer>
  )
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

/**
 * Long-term memory for a role moved to the knowledge base (memory migration
 * plan): pages under `roles/<role-slug>/` in the workspace space.
 */
function RoleKnowledgeNote({ roleName }: { roleName: string }) {
  const slug = kbSlug(roleName)
  const folder = `roles/${slug || '<role>'}/`
  return (
    <p className="mt-3 text-sm text-ink-3">
      Knowledge for this role lives in{' '}
      {slug ? (
        <Link
          to="/knowledge/$space/$"
          params={{ space: 'workspace', _splat: folder }}
          className="link"
        >
          Knowledge → workspace/{folder}
        </Link>
      ) : (
        <span>
          Knowledge → workspace/<span className="font-mono">{folder}</span>
        </span>
      )}
      . Saving to long-term memory no longer needs an approval.
    </p>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <fieldset className="space-y-2.5">
      <legend className="text-base font-medium text-ink">{title}</legend>
      {hint ? <p className="-mt-1 text-sm text-ink-3">{hint}</p> : null}
      {children}
    </fieldset>
  )
}

function toggle(list: string[], value: string, on: boolean): string[] {
  return on
    ? [...new Set([...list, value])]
    : list.filter((item) => item !== value)
}

/**
 * One list item per line; blank lines are dropped once the field is
 * committed. Keeps the raw text in local state and only splits/trims it on
 * blur — splitting on every keystroke used to drop a blank line (e.g. the
 * one a cursor sits on right after pressing Enter) as fast as it was typed,
 * making multi-line editing fight the field.
 */
function LinesField({
  id,
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  id: string
  value: string[]
  onChange: (lines: string[]) => void
  placeholder?: string
  rows?: number
}) {
  const [text, setText] = useState(() => value.join('\n'))

  // Follows a value that changed from outside (switching roles, restoring a
  // version, …) — this field's own edits never go through `value` until
  // blur, so this effect doesn't fight typing.
  useEffect(() => {
    setText(value.join('\n'))
  }, [value])

  function commit(raw: string): void {
    onChange(
      raw
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
    )
  }

  return (
    <textarea
      id={id}
      className="field-input"
      rows={rows}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
      placeholder={placeholder}
    />
  )
}

/**
 * A number input that only clamps to `[min, max]` on blur, not on every
 * keystroke — clamping live made it impossible to type past an
 * intermediate out-of-range value (e.g. typing "100" one digit at a time
 * when `min` is 10: the "1" alone would already snap to 10).
 */
function NumberField({
  id,
  value,
  onChange,
  min,
  max,
}: {
  id: string
  value: number
  onChange: (value: number) => void
  min: number
  max: number
}) {
  const [text, setText] = useState(() => String(value))

  useEffect(() => {
    setText(String(value))
  }, [value])

  function commit(raw: string): void {
    const next = Number(raw)
    const clamped = Number.isFinite(next) ? Math.min(max, Math.max(min, Math.round(next))) : value
    onChange(clamped)
    setText(String(clamped))
  }

  return (
    <input
      id={id}
      type="number"
      className="field-input"
      min={min}
      max={max}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
    />
  )
}

function RoleForm({
  workspaceId,
  roleId,
  values,
  onChange,
  onSubmit,
}: {
  workspaceId: string
  /** null while creating a role. */
  roleId: string | null
  values: RoleFormValues
  onChange: (values: RoleFormValues) => void
  onSubmit: (event: FormEvent) => void
}) {
  const [illustrations] = useIllustrations()
  const set = <K extends keyof RoleFormValues>(
    key: K,
    value: RoleFormValues[K]
  ) => onChange({ ...values, [key]: value })

  const runSettings = values.runSettings
  const setRunSettings = <K extends keyof RoleRunSettings>(key: K, value: RoleRunSettings[K]) =>
    set('runSettings', { ...runSettings, [key]: value })
  const setWeb = <K extends keyof RoleRunSettings['web']>(key: K, value: RoleRunSettings['web'][K]) =>
    setRunSettings('web', { ...runSettings.web, [key]: value })
  const setDelegate = <K extends keyof RoleRunSettings['delegate']>(key: K, value: RoleRunSettings['delegate'][K]) =>
    setRunSettings('delegate', { ...runSettings.delegate, [key]: value })
  const setTools = <K extends keyof RoleRunSettings['tools']>(key: K, value: RoleRunSettings['tools'][K]) =>
    setRunSettings('tools', { ...runSettings.tools, [key]: value })
  const setKb = <K extends keyof RoleRunSettings['kb']>(key: K, value: RoleRunSettings['kb'][K]) =>
    setRunSettings('kb', { ...runSettings.kb, [key]: value })
  const setReports = <K extends keyof RoleRunSettings['reports']>(
    key: K,
    value: RoleRunSettings['reports'][K]
  ) => setRunSettings('reports', { ...runSettings.reports, [key]: value })
  const setLimits = <K extends keyof RoleRunSettings['limits']>(key: K, value: RoleRunSettings['limits'][K]) =>
    setRunSettings('limits', { ...runSettings.limits, [key]: value })

  const skillsQuery = useQuery({
    queryKey: ['skills', workspaceId],
    queryFn: () => skillsApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const servicesQuery = useQuery({
    queryKey: ['mcp-services', workspaceId],
    queryFn: () => mcpServicesApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  // Names only (not the admin-only full list) — reachable by anyone who can
  // edit roles, so the picker works without the `search_connection.list`
  // permission (05 QA finding #9).
  const searchConnectionsQuery = useQuery({
    queryKey: ['search-connection-options', workspaceId],
    queryFn: () => searchConnectionsApi.options(workspaceId),
    enabled: workspaceId !== '',
  })
  const agentsQuery = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsApi.list({ workspaceId }),
    enabled: workspaceId !== '',
  })
  const skills = skillsQuery.data?.items ?? []
  const services = servicesQuery.data?.items ?? []
  const searchConnections = searchConnectionsQuery.data?.items ?? []

  return (
    <form id="role-form" className="space-y-7" onSubmit={onSubmit}>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Field label="Name" htmlFor="role-name">
          <input
            id="role-name"
            className="field-input"
            value={values.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="e.g. Security reviewer"
            required
          />
        </Field>
        <Field label="In one line" htmlFor="role-description">
          <input
            id="role-description"
            className="field-input"
            value={values.description}
            onChange={(event) => set('description', event.target.value)}
            placeholder="What this role does, for the list"
          />
        </Field>
      </div>

      <Field
        label="Instructions"
        htmlFor="role-instructions"
        hint="What the agent is told before every task: its job, how it works, what it must never do. The project’s own rules are added automatically."
      >
        <textarea
          id="role-instructions"
          className="field-input min-h-[180px] leading-relaxed"
          rows={9}
          value={values.baseInstructions}
          onChange={(event) => set('baseInstructions', event.target.value)}
          placeholder="You are the security reviewer. Read the diff with fresh eyes and look for…"
          required
        />
      </Field>

      <Section title="Kind of work">
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              {
                value: 'heavy',
                label: 'Builds and tests code',
                text: 'Gets a full sandbox and one of the few heavy slots.',
              },
              {
                value: 'light',
                label: 'Thinks and writes',
                text: 'Specs, reviews, plans. Many can run at once.',
              },
            ] as const
          ).map((option) => {
            const selected = values.workload === option.value
            return (
              <label
                key={option.value}
                className={`flex cursor-pointer gap-3 rounded-control border px-4 py-3 transition-colors ${
                  selected
                    ? 'border-action bg-action/5'
                    : 'border-line hover:border-line-strong'
                }`}
              >
                <input
                  type="radio"
                  name="workload"
                  className="mt-1"
                  checked={selected}
                  onChange={() => set('workload', option.value)}
                />
                <span>
                  <span className="block text-base font-medium text-ink">
                    {option.label}
                  </span>
                  <span className="block text-sm text-ink-3">
                    {option.text}
                  </span>
                </span>
              </label>
            )
          })}
        </div>
      </Section>

      <Section
        title="Riskiest task it may take on"
        hint="Tasks above this level are refused for agents with this role."
      >
        <div
          role="radiogroup"
          aria-label="Riskiest task"
          className="inline-flex flex-wrap rounded-control border border-line-strong bg-sunken p-0.5"
        >
          {RISK_LEVELS.map((level) => {
            const active = values.allowedRiskLevel === level.value
            return (
              <button
                key={level.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => set('allowedRiskLevel', level.value)}
                className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-surface text-ink shadow-sm'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                {illustrations ? (
                  <img
                    src={`/goblins/risk-${level.value}.webp`}
                    alt=""
                    aria-hidden
                    width={18}
                    height={18}
                    className="h-[18px] w-[18px]"
                  />
                ) : null}
                {level.label}
              </button>
            )
          })}
        </div>
      </Section>

      <Section
        title="Always ask first before"
        hint="On top of the workspace rules in Settings."
      >
        <ul className="grid gap-2 sm:grid-cols-2">
          {APPROVAL_ACTIONS.map((action) => {
            const id = `role-approval-${action.value}`
            return (
              <li key={action.value}>
                <label
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-control px-1 py-1 text-base text-ink"
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={values.approvalRequiredActions.includes(
                      action.value
                    )}
                    onChange={(event) =>
                      set(
                        'approvalRequiredActions',
                        toggle(
                          values.approvalRequiredActions,
                          action.value,
                          event.target.checked
                        )
                      )
                    }
                  />
                  <ClayIcon name={action.icon} size={24} />
                  {action.label}
                </label>
              </li>
            )
          })}
        </ul>
        <RoleKnowledgeNote roleName={values.name} />
      </Section>

      <Section title="Skills">
        {skills.length === 0 ? (
          <p className="text-sm text-ink-3">
            No skills yet.{' '}
            <Link to="/skills" className="link">
              Import skills
            </Link>{' '}
            to give roles extra know-how.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {skills.map((skill) => (
              <li key={skill.id}>
                <label className="flex cursor-pointer items-center gap-2.5 text-base text-ink">
                  <input
                    type="checkbox"
                    checked={values.skillIds.includes(skill.id)}
                    onChange={(event) =>
                      set(
                        'skillIds',
                        toggle(values.skillIds, skill.id, event.target.checked)
                      )
                    }
                  />
                  {skill.name}
                </label>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="MCP servers">
        {services.length === 0 ? (
          <p className="text-sm text-ink-3">
            No MCP servers yet. Once you add them on the{' '}
            <Link to="/mcp-services" className="link">
              MCP servers
            </Link>{' '}
            page, you choose here which ones this role may use. Each agent
            with this role then picks from these.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {services.map((service) => (
              <li key={service.id}>
                <label className="flex cursor-pointer items-center gap-2.5 text-base text-ink">
                  <input
                    type="checkbox"
                    checked={values.allowedMcpServiceIds.includes(service.id)}
                    onChange={(event) =>
                      set(
                        'allowedMcpServiceIds',
                        toggle(
                          values.allowedMcpServiceIds,
                          service.id,
                          event.target.checked
                        )
                      )
                    }
                  />
                  {service.name}
                </label>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Extra tools"
        hint="Off by default. Turn on only what this role's tasks actually need."
      >
        <div className="space-y-5">
          <div className="rounded-control border border-line p-3.5">
            <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={runSettings.web.enabled}
                onChange={(event) => setWeb('enabled', event.target.checked)}
              />
              <span>
                <span className="block font-medium">Web search and page reading</span>
                <span className="block text-sm text-ink-3">
                  Text from the web is untrusted — the agent is told not to follow instructions it finds there.
                </span>
              </span>
            </label>
            {runSettings.web.enabled ? (
              <div className="mt-3 space-y-3 pl-6">
                {values.allowedRiskLevel === 'high' || values.allowedRiskLevel === 'critical' ? (
                  <Notice tone="signal">
                    This role may take on high-risk tasks. Consider whether it should also read the open web.
                  </Notice>
                ) : null}
                <Field
                  label="Search connection"
                  htmlFor="role-search-connection"
                  hint="The connection this role searches with. Leave blank to use the workspace's first active one."
                >
                  <select
                    id="role-search-connection"
                    className="field-input"
                    value={runSettings.web.searchConnectionId ?? ''}
                    onChange={(event) => setWeb('searchConnectionId', event.target.value || null)}
                  >
                    <option value="">Workspace default</option>
                    {searchConnections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.name} ({SEARCH_PROVIDER_LABEL[connection.provider]})
                      </option>
                    ))}
                  </select>
                </Field>
                {/* Only when the WORKSPACE has no search connection at all —
                    not just when this role leaves it blank, which is the
                    normal, intended way to use the workspace's default
                    (05 QA finding #9). */}
                {searchConnections.length === 0 && !searchConnectionsQuery.isLoading ? (
                  <Notice tone="signal">
                    This workspace has no search connection yet. Add one on the{' '}
                    <Link to="/settings" className="link">
                      Settings
                    </Link>{' '}
                    page before turning this on.
                  </Notice>
                ) : null}
                <details>
                  <summary className="cursor-pointer text-sm font-medium text-ink-2">
                    Only these sites / Never these sites
                  </summary>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <Field
                      label="Only these sites"
                      htmlFor="role-web-allowed"
                      hint="One hostname per line. Empty means every public site."
                    >
                      <LinesField
                        id="role-web-allowed"
                        value={runSettings.web.allowedDomains}
                        onChange={(lines) => setWeb('allowedDomains', lines)}
                        placeholder="docs.example.com"
                      />
                    </Field>
                    <Field label="Never these sites" htmlFor="role-web-blocked" hint="One hostname per line.">
                      <LinesField
                        id="role-web-blocked"
                        value={runSettings.web.blockedDomains}
                        onChange={(lines) => setWeb('blockedDomains', lines)}
                        placeholder="*.internal.example.com"
                      />
                    </Field>
                  </div>
                </details>
              </div>
            ) : null}
          </div>

          <div className="rounded-control border border-line p-3.5">
            <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={runSettings.delegate.enabled}
                onChange={(event) => setDelegate('enabled', event.target.checked)}
              />
              <span>
                <span className="block font-medium">Sub-tasks</span>
                <span className="block text-sm text-ink-3">
                  Runs up to a few short, focused sub-tasks, using the run's own budget.
                </span>
              </span>
            </label>
            {runSettings.delegate.enabled ? (
              <div className="mt-3 grid gap-4 pl-6 sm:grid-cols-2">
                <Field label="Max at once" htmlFor="role-delegate-concurrent">
                  <NumberField
                    id="role-delegate-concurrent"
                    min={1}
                    max={3}
                    value={runSettings.delegate.maxConcurrent}
                    onChange={(value) => setDelegate('maxConcurrent', value)}
                  />
                </Field>
                <Field
                  label="Model"
                  htmlFor="role-delegate-model"
                  hint="A cheaper model for sub-tasks. Leave blank to use the agent's own model."
                >
                  <ModelPicker
                    id="role-delegate-model"
                    workspaceId={workspaceId}
                    target={values.defaultExecutorType ? { kind: values.defaultExecutorType } : null}
                    value={runSettings.delegate.model ?? ''}
                    onChange={(model) => setDelegate('model', model || null)}
                  />
                </Field>
              </div>
            ) : null}
          </div>

          <div>
            <span className="block text-base font-medium text-ink">Run tools</span>
            <p className="mt-1 text-sm text-ink-3">On by default. Turn off what this role shouldn't be able to do.</p>
            <div className="mt-2 space-y-2">
              <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.tools.askHuman}
                  onChange={(event) => setTools('askHuman', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Ask the person questions</span>
                  <span className="block text-sm text-ink-3">Pause and wait (up to 15 min) for an answer when it's genuinely stuck.</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.tools.requestApproval}
                  onChange={(event) => setTools('requestApproval', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Ask permission for exceptions</span>
                  <span className="block text-sm text-ink-3">Request sign-off before a risky or privileged action.</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.tools.postComment}
                  onChange={(event) => setTools('postComment', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Comment on the issue</span>
                  <span className="block text-sm text-ink-3">Post a short note on the task for humans tracking it.</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.tools.createFollowup}
                  onChange={(event) => setTools('createFollowup', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Propose follow-up tasks</span>
                  <span className="block text-sm text-ink-3">Create a task for real work found that's out of scope for this run.</span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.tools.createSubtasks}
                  onChange={(event) => setTools('createSubtasks', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Create subtasks</span>
                  <span className="block text-sm text-ink-3">Lets this role split a task into subtasks — give it to planners only.</span>
                </span>
              </label>
            </div>
          </div>

          <div className="rounded-control border border-line p-3.5">
            <label className="flex cursor-pointer items-start gap-2.5 text-base text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={runSettings.kb.enabled}
                onChange={(event) => setKb('enabled', event.target.checked)}
              />
              <span>
                <span className="block font-medium">Knowledge base</span>
                <span className="block text-sm text-ink-3">
                  Reads and searches the project and workspace knowledge pages, and gets the relevant ones in its prompt.
                </span>
              </span>
            </label>
            {runSettings.kb.enabled ? (
              <label className="mt-3 flex cursor-pointer items-start gap-2.5 pl-6 text-base text-ink">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={runSettings.kb.write}
                  onChange={(event) => setKb('write', event.target.checked)}
                />
                <span>
                  <span className="block font-medium">Write pages</span>
                  <span className="block text-sm text-ink-3">
                    Adds and updates pages. Folders that need review turn its changes into proposals.
                  </span>
                </span>
              </label>
            ) : null}
          </div>

          <div className="rounded-control border border-line p-3.5">
            <span className="block text-base font-medium text-ink">Reports</span>
            <p className="mt-0.5 text-sm text-ink-3">
              One report per task, for people: findings, choices, forms to fill in.
              Turn reading off to keep this role unbiased by earlier decisions.
            </p>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-base text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={runSettings.reports.read}
                onChange={(event) => setReports('read', event.target.checked)}
              />
              <span>
                <span className="block font-medium">Read reports</span>
                <span className="block text-sm text-ink-3">
                  The agent sees the task’s existing report and human form answers.
                </span>
              </span>
            </label>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-base text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={runSettings.reports.write}
                onChange={(event) => setReports('write', event.target.checked)}
              />
              <span>
                <span className="block font-medium">Write reports</span>
                <span className="block text-sm text-ink-3">
                  Writes new versions of the task’s report and attaches files (images, CSV, PDF) to it.
                </span>
              </span>
            </label>
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 text-base text-ink">
            <input
              type="checkbox"
              checked={runSettings.notebookEdit}
              onChange={(event) => setRunSettings('notebookEdit', event.target.checked)}
            />
            <span>
              Notebooks <span className="text-sm text-ink-3">(Claude Code only)</span>
            </span>
          </label>

          <div>
            <span className="block text-base font-medium text-ink">
              Extensions
            </span>
            <p className="mt-1 text-sm text-ink-3">No extensions are available yet.</p>
          </div>

          <Field
            label="Ask before these commands"
            htmlFor="role-ask-patterns"
            hint="On top of the worker's standard list. One pattern per line."
          >
            <LinesField
              id="role-ask-patterns"
              value={runSettings.askPatterns}
              onChange={(lines) => setRunSettings('askPatterns', lines)}
              placeholder="docker"
              rows={2}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="Claude Code prompt (A/B test)"
        hint="Temporary, while the A/B test decides between the two prompts. This setting goes away once that's decided."
      >
        <div
          role="radiogroup"
          aria-label="Claude Code prompt"
          className="inline-flex rounded-control border border-line-strong bg-sunken p-0.5"
        >
          {(
            [
              { value: 'native', label: 'Native' },
              { value: 'goblic', label: 'Goblic' },
            ] as const
          ).map((option) => {
            const active = runSettings.claudePrompt === option.value
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setRunSettings('claudePrompt', option.value)}
                className={`rounded-[6px] px-3 py-1 text-sm font-medium transition-colors ${
                  active ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      </Section>

      <Section
        title="When a usage limit is reached"
        hint="What a run does when its subscription or API runs out. It always keeps its work: a checkpoint is saved first."
      >
        <RoleQuotaSettings
          roleId={roleId}
          agents={agentsQuery.data?.items ?? []}
          onQuota={runSettings.onQuota}
          maxTransientRetries={runSettings.maxTransientRetries}
          onChange={(next) => setRunSettings('onQuota', next)}
          onRetriesChange={(next) => setRunSettings('maxTransientRetries', next)}
        />
      </Section>

      <Section title="Limits per run" hint="Safety nets, not a budget knob. Cost is only shown, never enforced.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Model calls" htmlFor="role-limit-calls">
            <NumberField
              id="role-limit-calls"
              min={1}
              max={2000}
              value={runSettings.limits.modelCalls}
              onChange={(value) => setLimits('modelCalls', value)}
            />
          </Field>
          <Field
            label="Tokens (thousands)"
            htmlFor="role-limit-tokens"
            hint={`${runSettings.limits.tokens.toLocaleString()} tokens`}
          >
            <NumberField
              id="role-limit-tokens"
              min={10}
              max={50_000}
              value={Math.round(runSettings.limits.tokens / 1000)}
              onChange={(value) => setLimits('tokens', value * 1000)}
            />
          </Field>
          {runSettings.web.enabled ? (
            <>
              <Field label="Web searches" htmlFor="role-limit-searches">
                <NumberField
                  id="role-limit-searches"
                  min={0}
                  max={200}
                  value={runSettings.limits.webSearches}
                  onChange={(value) => setLimits('webSearches', value)}
                />
              </Field>
              <Field label="Pages fetched" htmlFor="role-limit-fetches">
                <NumberField
                  id="role-limit-fetches"
                  min={0}
                  max={500}
                  value={runSettings.limits.webFetches}
                  onChange={(value) => setLimits('webFetches', value)}
                />
              </Field>
            </>
          ) : null}
          {runSettings.delegate.enabled ? (
            <Field label="Sub-tasks" htmlFor="role-limit-subtasks">
              <NumberField
                id="role-limit-subtasks"
                min={0}
                max={50}
                value={runSettings.limits.subtasks}
                onChange={(value) => setLimits('subtasks', value)}
              />
            </Field>
          ) : null}
        </div>
      </Section>

      <Section
        title="Defaults for new agents"
        hint="Suggested when someone sets up an agent with this role."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Executor" htmlFor="role-executor">
            <select
              id="role-executor"
              className="field-input"
              value={values.defaultExecutorType}
              // Another executor has other models: back to its default.
              onChange={(event) =>
                onChange({
                  ...values,
                  defaultExecutorType: event.target.value,
                  defaultModel: '',
                })
              }
            >
              {EXECUTORS.map((executor) => (
                <option key={executor.value} value={executor.value}>
                  {executor.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Model" htmlFor="role-model">
            <ModelPicker
              id="role-model"
              workspaceId={workspaceId}
              target={
                values.defaultExecutorType
                  ? { kind: values.defaultExecutorType }
                  : null
              }
              value={values.defaultModel}
              onChange={(model) => set('defaultModel', model)}
            />
          </Field>
        </div>
      </Section>
    </form>
  )
}

// ---------------------------------------------------------------------------
// After saving: move agents to the new version
// ---------------------------------------------------------------------------

function MoveAgentsNotice({
  workspaceId,
  roleId,
  version,
  currentVersionId,
}: {
  workspaceId: string
  roleId: string
  version: number
  currentVersionId: string
}) {
  const queryClient = useQueryClient()
  const agentsQuery = useQuery({
    queryKey: ['agents', workspaceId],
    queryFn: () => agentsApi.list({ workspaceId }),
  })
  const behind = (agentsQuery.data?.items ?? []).filter((agent) => {
    const a = agent as unknown as { roleId: string; roleVersionId?: string }
    return (
      a.roleId === roleId &&
      a.roleVersionId &&
      a.roleVersionId !== currentVersionId
    )
  })
  const move = useMutation({
    mutationFn: () =>
      Promise.all(
        behind.map((agent) =>
          agentsApi.update(agent.id, workspaceId, {
            roleVersionId: currentVersionId,
          })
        )
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] }),
  })

  if (move.isSuccess) {
    return (
      <Notice tone="success">
        Moved to version {version}. New runs use the new instructions.
      </Notice>
    )
  }
  if (behind.length === 0) {
    return <Notice tone="success">Saved as version {version}.</Notice>
  }
  return (
    <Notice tone="signal">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>
          Saved as version {version}.{' '}
          {behind.length === 1
            ? `${behind[0]!.name} still uses an older version.`
            : `${behind.length} agents still use an older version.`}
        </span>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => move.mutate()}
          disabled={move.isPending}
        >
          {move.isPending
            ? 'Moving…'
            : behind.length === 1
              ? 'Move it to the new version'
              : 'Move them to the new version'}
        </button>
      </div>
      {move.isError ? (
        <p className="mt-2 text-sm">
          {errorText(move.error, 'Couldn’t move the agents.')}
        </p>
      ) : null}
    </Notice>
  )
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function RoleHistory({
  workspaceId,
  roleId,
  currentVersion,
  onRestored,
}: {
  workspaceId: string
  roleId: string
  currentVersion: number
  onRestored: () => void | Promise<void>
}) {
  const { confirm, alert } = useDialogs()
  const versionsQuery = useQuery({
    queryKey: ['role-versions', roleId, workspaceId],
    queryFn: () => rolesApi.versions(roleId, workspaceId),
  })
  const versions = [...(versionsQuery.data?.versions ?? [])].sort(
    (a, b) => b.version - a.version
  )
  const [openVersion, setOpenVersion] = useState<number | null>(null)

  const restore = useMutation({
    mutationFn: (version: number) =>
      rolesApi.restore(roleId, version, workspaceId),
    onSuccess: () => void onRestored(),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t restore that version',
        message: errorText(error, ''),
      }),
  })

  async function handleRestore(version: RoleVersionRecord): Promise<void> {
    const ok = await confirm({
      kind: 'question',
      title: `Restore version ${version.version}?`,
      message: `A copy of it becomes version ${currentVersion + 1}. Version ${currentVersion} stays in the history.`,
      confirmLabel: 'Restore',
    })
    if (ok) restore.mutate(version.version)
  }

  if (versionsQuery.isLoading) return <LoadingRows />
  if (versionsQuery.isError)
    return <ErrorNote error={versionsQuery.error} what="the history" />

  return (
    <ol className="divide-y divide-line rounded-control border border-line">
      {versions.map((version) => {
        const isCurrent = version.version === currentVersion
        const expanded = openVersion === version.version
        return (
          <li key={version.id} className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                className="min-w-0 text-left"
                onClick={() =>
                  setOpenVersion(expanded ? null : version.version)
                }
                aria-expanded={expanded}
              >
                <span className="block text-base font-medium text-ink">
                  Version {version.version}
                  {isCurrent ? (
                    <span className="ml-2 text-sm font-normal text-ok">
                      current
                    </span>
                  ) : null}
                </span>
                <span className="block text-sm text-ink-3">
                  {formatDateTime(version.createdAt)}
                </span>
                <span className="block text-sm text-moss">
                  {expanded ? 'Hide instructions' : 'Show instructions'}
                </span>
              </button>
              {!isCurrent ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm shrink-0"
                  onClick={() => void handleRestore(version)}
                  disabled={restore.isPending}
                >
                  Restore
                </button>
              ) : null}
            </div>
            {expanded ? (
              <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-control bg-sunken p-3 text-sm leading-relaxed text-ink-2">
                {version.baseInstructions}
              </pre>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
